import React from 'react';

import OperondaLogo from '@src/components/OperondaLogo';
import StepIndicator from '@src/components/StepIndicator';
import IntlMessages from '@src/util/IntlMessages';

import { MarkingPortalIdentificationForm } from './components/MarkingPortalIdentificationForm';
import { MarkingPortalConfirmModal } from './components/MarkingPortalConfirmModal';
import { MarkingPortalMarkingPanel } from './components/MarkingPortalMarkingPanel';
import { MarkingPortalPinModal } from './components/MarkingPortalPinModal';
import { MARKING_PORTAL_TOTAL_STEPS, useMarkingPortal } from './hooks/useMarkingPortal';
import styles from './styles.module.less';

const MarkingPortalView: React.FC = () => {
  const {
    form,
    step,
    session,
    currentStep,
    isSubmitting,
    isRegistering,
    sessionSecondsRemaining,
    isPinModalOpen,
    pinModalEmail,
    pinValue,
    isSendingPin,
    isSubmittingPin,
    isResendingPin,
    pinErrorMessageId,
    onIdentify,
    onSendPin,
    onSubmitPin,
    onResendPin,
    onClosePinModal,
    onPinChange,
    onRegisterMarking,
    onConfirmRegisterMarking,
    onCloseConfirmModal,
    onBackToStartFromConfirm,
    isConfirmModalOpen,
    confirmModalPhase,
    confirmModalCopy,
    pendingMarkingAtDisplay,
    earlyMarkingWarningId,
    successResetSecondsRemaining,
    onFinishShift,
    onLogout,
  } = useMarkingPortal();

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className="gx-flex-row gx-justify-content-center">
          <OperondaLogo />
        </div>

        <div className={styles.content}>
          <div className={styles.pageHeaderRow}>
            <h1 className={styles.pageTitle}>
              <IntlMessages id="markingPortal.title" />
            </h1>

            {step === 'marking' && (
              <p className={styles.sessionTimer}>
                <IntlMessages
                  id="markingPortal.session.expiresIn"
                  values={{ seconds: sessionSecondsRemaining }}
                />
              </p>
            )}
          </div>

          <StepIndicator
            layout="inline"
            currentStep={currentStep}
            totalSteps={MARKING_PORTAL_TOTAL_STEPS}
            stepCounterId="markingPortal.step"
            showStepCounter
          />

          {step === 'identify' ? (
            <MarkingPortalIdentificationForm
              form={form}
              isSubmitting={isSubmitting}
              isSendingPin={isSendingPin}
              onIdentify={onIdentify}
              onSendPin={onSendPin}
            />
          ) : (
            session && (
              <MarkingPortalMarkingPanel
                onLogout={onLogout}
                session={session}
                isRegistering={isRegistering}
                onRegisterMarking={onRegisterMarking}
                onFinishShift={onFinishShift}
              />
            )
          )}
        </div>
      </div>

      <MarkingPortalPinModal
        open={isPinModalOpen}
        email={pinModalEmail}
        pin={pinValue}
        isSubmitting={isSubmittingPin}
        isResending={isResendingPin}
        errorMessageId={pinErrorMessageId}
        onPinChange={onPinChange}
        onSubmit={onSubmitPin}
        onResend={onResendPin}
        onClose={onClosePinModal}
      />

      {confirmModalCopy && session && (
        <MarkingPortalConfirmModal
          open={isConfirmModalOpen}
          phase={confirmModalPhase}
          copy={confirmModalCopy}
          displayName={session.displayName}
          markingAtDisplay={pendingMarkingAtDisplay}
          isSubmitting={isRegistering}
          resetSecondsRemaining={successResetSecondsRemaining}
          earlyWarningId={earlyMarkingWarningId}
          onConfirm={onConfirmRegisterMarking}
          onBackToStart={onBackToStartFromConfirm}
          onClose={onCloseConfirmModal}
        />
      )}
    </div>
  );
};

export const MarkingPortal = MarkingPortalView;
